'use client'

import { useCallback, useRef, useState } from 'react'

type WebcamStatus = 'idle' | 'requesting' | 'active' | 'denied' | 'error'

interface UseWebcamRecorderReturn {
  status: WebcamStatus
  error: string | null
  stream: MediaStream | null
  /** Callback ref — attach to any <video> to show the live camera feed */
  attachVideo: (el: HTMLVideoElement | null) => void
  /** Request camera permission and start the live preview + recording */
  startCamera: () => Promise<boolean>
  /** Stop camera and return a Blob of the recorded video (webm) */
  stopCamera: () => Blob | null
  /** Stop recording, upload to R2, return the public URL (or null on failure) */
  stopAndUpload: () => Promise<string | null>
  /** Stop camera without returning a blob */
  cleanup: () => void
}

export function useWebcamRecorder(): UseWebcamRecorderReturn {
  const [status, setStatus] = useState<WebcamStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [stream, setStream] = useState<MediaStream | null>(null)

  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])

  // Callback ref: attaches the live stream to any <video> element it's set on.
  // Works across mount/unmount cycles (modal → interview view).
  const attachVideo = useCallback((el: HTMLVideoElement | null) => {
    if (el && streamRef.current) {
      el.srcObject = streamRef.current
      el.play().catch(() => {})
    }
  }, [])

  const startCamera = useCallback(async (): Promise<boolean> => {
    setStatus('requesting')
    setError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: true,
      })

      streamRef.current = stream
      setStream(stream)

      // Start recording
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
        ? 'video/webm;codecs=vp8,opus'
        : 'video/webm'

      chunksRef.current = []
      const recorder = new MediaRecorder(stream, { mimeType })
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }
      recorderRef.current = recorder
      recorder.start(5000) // collect data every 5s

      setStatus('active')
      return true
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Camera access failed'
      // Distinguish permission denied from other errors
      if (
        err instanceof DOMException &&
        (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError')
      ) {
        setStatus('denied')
        setError('Camera permission denied')
      } else {
        setStatus('error')
        setError(msg)
      }
      return false
    }
  }, [])

  const stopTracks = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    setStream(null)
  }, [])

  /** Stop recorder gracefully — waits for the final dataavailable event */
  const stopRecorder = useCallback(async (): Promise<Blob | null> => {
    const recorder = recorderRef.current
    const stream = streamRef.current

    if (!recorder || recorder.state === 'inactive') {
      // Recorder already stopped — no finalized webm available
      stream?.getTracks().forEach((t) => t.stop())
      streamRef.current = null
      setStream(null)
      return null
    }

    // Wait for the final chunk + webm finalization segment
    await new Promise<void>((resolve) => {
      recorder.onstop = () => resolve()
      recorder.stop()
    })
    recorderRef.current = null

    // Stop camera tracks after recorder has flushed
    stream?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    setStream(null)

    if (chunksRef.current.length === 0) return null
    const mimeType = recorder.mimeType || 'video/webm'
    const blob = new Blob(chunksRef.current, { type: mimeType })
    chunksRef.current = []
    return blob
  }, [])

  const stopCamera = useCallback((): Blob | null => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop() } catch { /* already stopped */ }
    }
    recorderRef.current = null
    stopTracks()
    setStatus('idle')
    if (chunksRef.current.length === 0) return null
    const blob = new Blob(chunksRef.current, { type: 'video/webm' })
    chunksRef.current = []
    return blob
  }, [stopTracks])

  const stopAndUpload = useCallback(async (): Promise<string | null> => {
    const blob = await stopRecorder()
    setStatus('idle')
    if (!blob) return null

    const mimeType = blob.type || 'video/webm'
    const filename = `webcam-${Date.now()}-${Math.random().toString(36).slice(2)}.webm`

    try {
      const urlRes = await fetch('/api/kolleno/get-upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename, contentType: mimeType }),
      })

      if (!urlRes.ok) return null

      const { uploadUrl, publicUrl } = await urlRes.json()

      for (let attempt = 0; attempt < 2; attempt++) {
        const res = await fetch(uploadUrl, {
          method: 'PUT',
          body: blob,
          headers: { 'Content-Type': mimeType },
        })
        if (res.ok) return publicUrl
      }
    } catch (err) {
      console.error('[useWebcamRecorder] upload failed:', err)
    }
    return null
  }, [stopRecorder])

  const cleanup = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop() } catch { /* already stopped */ }
    }
    recorderRef.current = null
    stopTracks()
    chunksRef.current = []
    setStatus('idle')
    setError(null)
  }, [stopTracks])

  return { status, error, stream, attachVideo, startCamera, stopCamera, stopAndUpload, cleanup }
}
